<template>
    <div id="js-billing-setup-page">
        <div class="row page-title">
            <div class="col-md-12">
                <h1>Billing</h1>
            </div>
        </div>

        <div class="row page-title">
            <div class="col-md-12">
                <ul class="nav nav-tabs">
                    <li :class="{active: isStep('manage-cards')}">
                        <a href="#manage-cards" @click.prevent="goto('manage-cards')">
                            <strong v-if="isStep('manage-cards')">Funding Sources</strong> <span v-else>Funding Sources</span>
                        </a>
                    </li>

                    <li v-show="showPaymentPlans" :class="{active: isStep('select-payment-plan'), disabled: hasntAddedACardYet}">
                        <a href="#select-payment-plan" @click.prevent="goto('select-payment-plan')">
                            <strong v-if="isStep('select-payment-plan')">Payment Plan</strong> <span v-else>Payment Plan</span>
                        </a>
                    </li>

                    <li :class="{active: isStep('add-funds'), disabled: hasntAddedACardYet}">
                        <a href="#add-funds" @click.prevent="goto('add-funds')">
                            <strong v-if="isStep('add-funds')">Add Funds</strong> <span v-else>Add Funds</span>
                        </a>
                    </li>

                    <li :class="{active: isStep('payment-history'), disabled: hasntAddedACardYet}">
                        <a href="#payment-history" @click.prevent="goto('payment-history')">
                            <strong v-if="isStep('payment-history')">Funding History</strong> <span v-else>Funding History</span>
                        </a>
                    </li>

                    <li v-show="showCreditLine" :class="{active: isStep('credit-line')}">
                        <a href="#credit-line" @click.prevent="goto('credit-line')">
                            <strong v-if="isStep('credit-line')">Credit Line</strong> <span v-else>Credit Line</span>
                        </a>
                    </li>
                </ul>
            </div>
        </div>

        <div class="row">
            <div class="col-md-12">
                <div class="tab-content">
                    <div class="tab-pane active">
                        <keep-alive>
                            <component
                                :is="currentComponent"
                                :loading="loading"
                                :credit-line="creditLine"
                                :funds-pools="fundsPools"
                                :funding-minimums-by-product-group="fundingMinimumsByProductGroup"
                                :funding-sources="fundingSources"
                                :card-types="cardTypes"
                                :payment-plans="paymentPlans"
                                :funds-pools-for-credits="fundsPoolsForCredits"
                                :non-invoiced-payment-plans="nonInvoicedPaymentPlans"
                                :non-invoiced-funds-pools="nonInvoicedFundsPools"
                                :payflow-link-url="payflowLinkUrl"
                                @refreshStep="refresh"
                            ></component>
                        </keep-alive>
                    </div>
                </div>
            </div>
        </div>

        <div class="row" v-cloak>
            <div class="col-sm-8 col-sm-offset-2 text-center p-t-lg p-b-lg">
                <strong>Paying by Direct Bill?</strong>
                <p>Please contact your
                    <a href="mailto:support@bankrate.com">support@bankrate.com</a> for instructions on our Insertion Order process.</p>

                <strong>Changing Payment Method</strong>
                <p>If you wish to change your method of payment from Direct Bill to Credit Card or Credit Card to Direct Bill, please contact your
                    <a href="mailto:support@bankrate.com">support@bankrate.com</a>.
                </p>
            </div>
        </div>
    </div>
</template>

<script type="text/babel">
    import { queryParam } from '../../../js/utils';
    import Swal from "sweetalert2";

    export default {
        props: ['fundingMinimumsByProductGroup', 'payflowLinkUrl'],

        data() {
            return {
                step: 'manage-cards',
                loading: true,
                cards: [],
                fundingSources: [],
                cardTypes: [],
                creditLine: {},
                fundsPools: [],
                paymentPlans: [],
                fundsPoolsForCredits: [],
            }
        },

        mounted() {
            Promise.all([
                this.getFundingSources(),
                this.getCardTypes(),
                this.getPaymentPlans(),
                this.getFundsPools()
            ]).catch(() => {
                new Swal('Whoops!', 'Something went wrong. Please contact support.', 'error');
            }).then(() => {
                this.filterFundsPoolsByPaymentPlanType()

                this.goto(queryParam('step') || this.step);

                this.loading = false;
            })
        },

        computed: {
            currentComponent() {
                return 'billing-' + this.step;
            },

            showCreditLine() {
                return this.fundsPoolsForCredits.length !== 0;
            },

            hasntAddedACardYet() {
                return this.cards.length === 0;
            },

            nonInvoicedFundsPools() {
                return this.fundsPools.filter((i) => {
                    let relatedPaymentPlan = _(this.paymentPlans).find({ fundsPoolId: i.id });
                    if (!relatedPaymentPlan) {
                        return false;
                    }

                    return relatedPaymentPlan.type !== 'Direct-Bill' && relatedPaymentPlan.type !== 'Auto-Renewing-Credit'
                });
            },

            nonInvoicedPaymentPlans() {
                return this.paymentPlans.filter((i) => {
                    return i.type !== 'Direct-Bill' && i.type !== 'Auto-Renewing-Credit'
                });
            },

            showPaymentPlans() {
                return this.nonInvoicedPaymentPlans.length > 0;
            },
        },

        methods: {
            goto(step) {
                if (this.hasntAddedACardYet && (step === 'select-payment-plan' || step === 'add-funds' || step === 'payment-history')) {
                    return;
                }

                this.step = step;
            },

            refresh() {
                window.location = window.location.href.split("?")[0] + '?step=' + this.step;
            },

            isStep(step) {
                return this.step === step;
            },

            getFundingSources() {
                return this.$http.get(route('billing-setup.funding-source.index'))
                    .then(({ data }) => {
                        this.cards = data.fundingSources;
                        this.fundingSources = data.fundingSources;
                    });
            },

            getCardTypes() {
                return this.$http.get(route('billing-setup.credit-card-type.index'))
                    .then(({ data }) => {
                        this.cardTypes = data.creditCardTypes;
                    });
            },

            getFundsPools() {
                return this.$http.get(route('billing-setup.funds-pool.index'))
                  .then(({ data }) => {
                      this.fundsPools = data.fundsPools;
                  });
            },

            getPaymentPlans() {
                return this.$http.get(route('billing-setup.payment-plan.index'))
                  .then(({ data }) => {
                      this.paymentPlans = data.paymentPlans;
                  });
            },

            filterFundsPoolsByPaymentPlanType() {
                this.fundsPoolsForCredits = this.fundsPools.filter((i) => {
                    let relatedPaymentPlan = _(this.paymentPlans).find({ fundsPoolId: i.id });
                    return relatedPaymentPlan.type === 'Direct-Bill' || relatedPaymentPlan.type === 'Auto-Renewing-Credit'
                });
            }
        }
    }
</script>
